/**
 * SVG path builders for line and area charts. A point with `y === null` is a GAP: the line breaks there instead of
 * dipping to zero. Coordinates are rounded to two decimals so the output is short and stable.
 */
import { isFiniteNumber, roundCoord } from './numbers.js';
/** Split into runs of consecutive drawable points (a gap, or a non-finite coordinate, ends a run). */
export function drawableRuns(points) {
    const runs = [];
    let run = [];
    for (const p of points) {
        if (p.y !== null && isFiniteNumber(p.x) && isFiniteNumber(p.y))
            run.push({ x: p.x, y: p.y });
        else if (run.length > 0) {
            runs.push(run);
            run = [];
        }
    }
    if (run.length > 0)
        runs.push(run);
    return runs;
}
function n(value) {
    return String(roundCoord(value));
}
function polylineRun(run) {
    return run.map((p, i) => `${i === 0 ? 'M' : 'L'}${n(p.x)} ${n(p.y)}`).join('');
}
/**
 * Tangents of a monotone cubic (Fritsch–Carlson): between two points the curve never leaves their y range, so a smooth
 * line cannot overshoot a peak or dip below a zero floor.
 */
function monotoneTangents(run) {
    const count = run.length;
    const dx = [];
    const slope = [];
    for (let i = 0; i < count - 1; i += 1) {
        const a = run[i];
        const b = run[i + 1];
        const w = b.x - a.x;
        dx.push(w);
        slope.push(w === 0 ? 0 : (b.y - a.y) / w);
    }
    const m = new Array(count).fill(0);
    m[0] = slope[0] ?? 0;
    m[count - 1] = slope[count - 2] ?? 0;
    for (let i = 1; i < count - 1; i += 1) {
        const s0 = slope[i - 1];
        const s1 = slope[i];
        m[i] = s0 * s1 <= 0 ? 0 : (s0 + s1) / 2;
    }
    for (let i = 0; i < count - 1; i += 1) {
        const s = slope[i];
        if (s === 0) {
            m[i] = 0;
            m[i + 1] = 0;
            continue;
        }
        const a = m[i] / s;
        const b = m[i + 1] / s;
        const h = a * a + b * b;
        if (h > 9) {
            const t = 3 / Math.sqrt(h);
            m[i] = t * a * s;
            m[i + 1] = t * b * s;
        }
    }
    return m;
}
function smoothRun(run) {
    if (run.length < 3)
        return polylineRun(run);
    const m = monotoneTangents(run);
    let d = `M${n(run[0].x)} ${n(run[0].y)}`;
    for (let i = 0; i < run.length - 1; i += 1) {
        const a = run[i];
        const b = run[i + 1];
        const third = (b.x - a.x) / 3;
        d += `C${n(a.x + third)} ${n(a.y + m[i] * third)} ${n(b.x - third)} ${n(b.y - m[i + 1] * third)} ${n(b.x)} ${n(b.y)}`;
    }
    return d;
}
/** A straight-segment line. No drawable points returns `''`; an isolated point is a bare `M x y` (draw it as a dot). */
export function linePath(points) {
    return drawableRuns(points).map(polylineRun).join('');
}
/** A smooth line (monotone cubic — never overshoots the data). Runs of one or two points fall back to straight segments. */
export function smoothLinePath(points) {
    return drawableRuns(points).map(smoothRun).join('');
}
/**
 * The closed area between the line and the horizontal `baselineY`, one closed shape per run. An isolated point has no
 * area. Fill it under the line; the line itself is a separate stroke.
 */
export function areaPath(points, baselineY, options = {}) {
    if (!isFiniteNumber(baselineY))
        return '';
    return drawableRuns(points)
        .filter((run) => run.length >= 2)
        .map((run) => {
        const top = options.smooth === true ? smoothRun(run) : polylineRun(run);
        const last = run[run.length - 1];
        const first = run[0];
        return `${top}L${n(last.x)} ${n(baselineY)}L${n(first.x)} ${n(baselineY)}Z`;
    })
        .join('');
}
/** The drawable points that stand alone (no neighbour on either side), which a line cannot show. Draw them as dots. */
export function isolatedPoints(points) {
    return drawableRuns(points)
        .filter((run) => run.length === 1)
        .map((run) => run[0]);
}
/** A rounded-top rectangle as a path, for bars whose top corners are rounded and whose base sits flat on the baseline. */
export function roundedTopBarPath(x, y, width, height, radius) {
    if (!(width > 0) || !(height > 0))
        return '';
    const r = Math.max(0, Math.min(radius, width / 2, height));
    if (r === 0)
        return `M${n(x)} ${n(y)}h${n(width)}v${n(height)}h${n(-width)}Z`;
    return `M${n(x)} ${n(y + height)}V${n(y + r)}A${n(r)} ${n(r)} 0 0 1 ${n(x + r)} ${n(y)}H${n(x + width - r)}A${n(r)} ${n(r)} 0 0 1 ${n(x + width)} ${n(y + r)}V${n(y + height)}Z`;
}
//# sourceMappingURL=path.js.map